//=============================================
// ARCHELIOS MAP NAMESPACE
//=============================================
var am = (function(module) {

    //Use strict mode
    'use strict';

    /**
     * Class used to compute the solar potential of a roof composed of a collection of pixels
     */
    module.PixelizedRoof = function() {
        // Private attributes
        this._params = undefined;
        this._maxSurfaceIrradPourcent =null;//#202
        this._properties = {};
    };

    /**
     * Class constructor
     * @constructor
     *
     * @param {Object} config - Object holding the project configuration. The following key is required:
     *      - solarPotentialBins: Array of 3 values specifying the limits of the 4 solar potential bins to be
     *          considered when displaying information on a roof 
     * @param {Object} params - Object configuration. The following keys are required:
     *      - surfaceUsageCoefficient: [0-1] Maximum percentage of a roof surface area on which solar panels can be 
     *          installed
     *
     */
    module.PixelizedRoof.prototype.init = function(config, params) {
        this._params = params;
        this._properties.pvPanelYield = (params !== undefined) ? params.pvPanelYield : 0.18;
        this._properties.irradiationClasses = am.utils.getIrradClasses(config);
        this._properties.maxOptimalPotential = config.irradiation.maxValue;

        // Compute the classes of irradiation
        var irradiationClassLimits = [];
        config.irradiation.classes.forEach(function(irradClass) {
            var maxValue = irradClass.upperLimit;
            if (irradClass.units == "%") {
                maxValue = maxValue / 100 * config.irradiation.maxValue;
            } else if (irradClass.units != "kWh") {
                throw new Error("Unknown units '" + irradClass.units + "' in key irradiation.classes of the configuration file");
            }
            irradiationClassLimits.push(maxValue);
        });

        if(config.irradiation.hasOwnProperty("ficheBati")&&config.irradiation.ficheBati.hasOwnProperty("maxSurfaceIrradPourcent")){//#202
            this._maxSurfaceIrradPourcent=config.irradiation.ficheBati.maxSurfaceIrradPourcent;
        }
        irradiationClassLimits.sort(function(a, b) { return a - b; }); // ascending order
        irradiationClassLimits[irradiationClassLimits.length - 1] = Infinity; // replace last value with infinity
        this._irradiationClassLimits = irradiationClassLimits;
    };

    /**
     * Class destructor
     */
    module.PixelizedRoof.prototype.destroy = function() {
        this._params = undefined;
        this._properties = {};
    };

    module.PixelizedRoof.prototype.setRoofInfos = function(roofInfos) {
        var self = this;

        // Complete missing fields for backwards compatibility
        if (roofInfos.hasOwnProperty("dis_pos_bt") && roofInfos.hasOwnProperty("dis_res_bt")) {
            roofInfos.dist_poste = roofInfos.dis_pos_bt;
            roofInfos.dist_res = roofInfos.dis_res_bt;
        } else if (roofInfos.hasOwnProperty("DIS_POS_BT") && roofInfos.hasOwnProperty("DIS_RES_BT")) {
            roofInfos.dist_poste = roofInfos.DIS_POS_BT;
            roofInfos.dist_res = roofInfos.DIS_RES_BT;
        }
        if (roofInfos.hasOwnProperty('usage') && !roofInfos.hasOwnProperty('type_bati')) {
            roofInfos.type_bati = roofInfos.usage;
        }

        // Store general information about the roof in variable this._properties
        this._properties.roofId = roofInfos.id;
        this._properties.roofGroundArea = roofInfos.roofGroundArea;
        this._properties.buildingGroundArea = roofInfos.hasOwnProperty("buildingGroundArea") ?
            roofInfos.buildingGroundArea : roofInfos.roofGroundArea;
        this._properties.surfaceType = 'roof';
        this._properties.mountingType = 'onRoofPlane';
        this._properties.surfaceUsageCoefficient = {
            pv: this._getSurfaceUsageCoefficient('pv', 'tiltedRoof'),
            thermal: this._getSurfaceUsageCoefficient('thermal', 'tiltedRoof')
        };
        if (roofInfos.hasOwnProperty('type_bati')) {
            var surfaceType = roofInfos.type_bati.toLowerCase();
            if (surfaceType === "parkings") surfaceType = "parking";
            if (surfaceType === 'parking' || surfaceType === 'ground') {
                this._properties.surfaceType = surfaceType;
                this._properties.mountingType = 'onMountingSystem';
                this._properties.surfaceUsageCoefficient = {
                    pv: this._getSurfaceUsageCoefficient('pv', surfaceType),
                    thermal: this._getSurfaceUsageCoefficient('thermal', surfaceType)
                };
            }
        }
        if (roofInfos.hasOwnProperty("lowerLeftCorner") && roofInfos.hasOwnProperty("upperRightCorner")) {
            this._properties.roofExtents = {
                lowerLeftCorner: roofInfos.lowerLeftCorner,
                upperRightCorner: roofInfos.upperRightCorner
            };
        } else {
            this._properties.roofExtents = {
                lowerLeftCorner: [1.500, 42.600],
                upperRightCorner: [1.5001, 42.6001]
            };
        }
        this._properties.meanRoofOrientation = undefined;
        this._properties.meanRoofSlope = undefined;
        if(roofInfos.hasOwnProperty("zone_ABF")){
            roofInfos.architecturalConstraints = roofInfos.zone_ABF;
        }
        if (roofInfos.hasOwnProperty("architecturalConstraints")) {
            this._properties.architecturalConstraints = roofInfos.architecturalConstraints;
        }
        if (roofInfos.hasOwnProperty("dist_poste") && roofInfos.hasOwnProperty("dist_res")) {
            var distances = this._params.hasOwnProperty('electricalNetworkDistanceThresholds') ?
                this._params.electricalNetworkDistanceThresholds : { alongNetwork: [125, 250], toNetwork: [10, 100] };
            this._properties.electricalNetworkConnexionContraints =
                (roofInfos.dist_poste <= distances.alongNetwork[0] && roofInfos.dist_res <= distances.toNetwork[0]) ? 1 :
                    (roofInfos.dist_poste <= distances.alongNetwork[1] && roofInfos.dist_res <= distances.toNetwork[1]) ? 2 : 3;
        } else {
            this._properties.electricalNetworkConnexionContraints = 1;
        }
        if (roofInfos.hasOwnProperty("commune")) {
            this._properties.cityCode = roofInfos.commune;
        }
        
        // Collect information on the roof pixels (irradiation, orientation and tilt of each pixel)
        this._roofPixels = [];
        var roof3DArea = 0;
        var maxIrradiation=-Infinity;//#202
        for (var iPixel = 0, nPixels = roofInfos.irradiation.length; iPixel < nPixels; ++iPixel) {
            var pixelIrradiation = (this._properties.surfaceType === 'parking' && this._properties.mountingType === 'onMountingSystem' && roofInfos.hasOwnProperty('irradiationTilt5Deg')) ? roofInfos.irradiationTilt5Deg[iPixel] :
                                   (this._properties.surfaceType === 'ground' && this._properties.mountingType === 'onMountingSystem' && roofInfos.hasOwnProperty('irradiationTilt15Deg')) ? roofInfos.irradiationTilt15Deg[iPixel] :
                                   roofInfos.irradiation[iPixel];
            var pixelTilt = roofInfos.tilt[iPixel];
            if (Math.abs(pixelIrradiation - roofInfos.noDataValue) > 1e-3) { // irradiation is not NODATA
                var pixel3DArea = this._properties.mountingType === 'onRoofPlane' ? 1 / Math.cos(pixelTilt * Math.PI / 180) : 1; // will be multiplied by the ground area of one pixel later on
                var roofPixelInfo = {
                    irradiation: pixelIrradiation,
                    orientation: roofInfos.orientation[iPixel],
                    tilt: pixelTilt,
                    area3D: pixel3DArea
                };
                if (roofInfos.hasOwnProperty('irradiationTilt15Deg')) roofPixelInfo.irradiationTilt15Degrees = roofInfos.irradiationTilt15Deg[iPixel];
                if (roofInfos.hasOwnProperty('irradiationTilt60Deg')) roofPixelInfo.irradiationTilt60Degrees = roofInfos.irradiationTilt60Deg[iPixel];
                this._roofPixels.push(roofPixelInfo);
                roof3DArea += pixel3DArea;
                maxIrradiation = Math.max(maxIrradiation,pixelIrradiation);
            }
        }
        this._properties.maxIrradiation=maxIrradiation;//#202
       
        if(this._maxSurfaceIrradPourcent!=null){//#202
            var surfaceOverPourcent=0;
            
            for (var iPixel = 0, nPixels = roofInfos.irradiation.length; iPixel < nPixels; ++iPixel) {
                var pixelIrradiation = roofInfos.irradiation[iPixel];
                if (Math.abs(pixelIrradiation - roofInfos.noDataValue) > 1e-3) {
                    if(pixelIrradiation>=this._maxSurfaceIrradPourcent/100*maxIrradiation){
                        surfaceOverPourcent++;
                    }
                }
            }
            this._properties.surfaceOverPourcent=surfaceOverPourcent;
        }

        // Compute the ground surface area of one pixel, and correct the 3D surface of the pixels computed above
        var pixelGroundSurface = this._properties.pixelGroundSurface = roofInfos.roofGroundArea / this._roofPixels.length;
        roof3DArea *= pixelGroundSurface;
        this._properties.roof3DArea = (roofInfos.roof3DArea !== undefined) ? roofInfos.roof3DArea : roof3DArea;
        var correctionFactor = this._properties.roof3DArea / roof3DArea;
        for (var iPixel = 0, nPixels = this._roofPixels.length; iPixel < nPixels; ++iPixel) {
            this._roofPixels[iPixel].area3D *= pixelGroundSurface * correctionFactor;
        }

        // Sort the pixel information in descending irradiation value order
        this._roofPixels.sort(function(a, b) {
            return b.irradiation - a.irradiation;
        });

        // Get the surface per irradiation class
        this._properties.surfacePerIrradiationClass = [];
        for (var iIrradClass = 0, nIrradiationClasses = this._irradiationClassLimits.length; iIrradClass < nIrradiationClasses; ++iIrradClass) {
            var surface = 0;
            this._roofPixels.forEach(function(pixel) {
                if (pixel.irradiation < self._irradiationClassLimits[iIrradClass] &&
                        (iIrradClass === 0 || pixel.irradiation >= self._irradiationClassLimits[iIrradClass - 1])) {
                    surface += pixel.area3D;
                }
            });
            this._properties.surfacePerIrradiationClass.push(surface);
        }

        // Compute the min and max available roof surface and installation size
        this._properties.minAvailableRoof3DArea = 0 /*m²*/;
        this._properties.maxAvailableRoof3DArea = this._properties.roof3DArea;
        this._properties.minAvailablePanelSurface = {
            pv: 0,
            thermal: 0
        };
        this._properties.maxAvailablePanelSurface = {
            pv: this._properties.maxAvailableRoof3DArea * this._properties.surfaceUsageCoefficient.pv,
            thermal: this._properties.maxAvailableRoof3DArea * this._properties.surfaceUsageCoefficient.thermal
        };
    };

    module.PixelizedRoof.prototype.computeAnnualIrradiation = function(userInfos, installationType) {
        installationType = installationType || "pv"; // PV or thermal

        // Correct the available panel surface in case of flat roofs
        if (this._properties.surfaceType === 'roof') {
            var roofType = (userInfos.hasOwnProperty('isFlatRoof') && userInfos.isFlatRoof) ? 'flatRoof' : 'tiltedRoof';
            this._properties.mountingType = roofType === 'flatRoof' ? 'onMountingSystem' : 'onRoofPlane';
            this._properties.maxAvailableRoof3DArea = roofType === 'flatRoof' ? this._properties.roofGroundArea : this._properties.roof3DArea;
            this._properties.surfaceUsageCoefficient = {
                pv: this._getSurfaceUsageCoefficient('pv', roofType),
                thermal: this._getSurfaceUsageCoefficient('thermal', roofType)
            };
            this._properties.maxAvailablePanelSurface = {
                pv: this._properties.maxAvailableRoof3DArea * this._properties.surfaceUsageCoefficient.pv,
                thermal: this._properties.maxAvailableRoof3DArea * this._properties.surfaceUsageCoefficient.thermal
            };
        }

        // Compute the used roof area from the panel surface
        var minAvailablePanelSurface = this._properties.minAvailablePanelSurface[installationType];
        var maxAvailablePanelSurface = this._properties.maxAvailablePanelSurface[installationType];
        var surfaceUsageCoefficient = this._properties.surfaceUsageCoefficient[installationType];
        var panelSurface = Math.max(minAvailablePanelSurface, Math.min(userInfos.panelSurface, maxAvailablePanelSurface));
        var usedRoofArea = panelSurface / surfaceUsageCoefficient;

        // Compute the mean irradiation over the used roof area
        var irradiationDataSet = (this._properties.mountingType === 'onRoofPlane' || this._properties.surfaceType !== 'roof') ? 'irradiation' :
            (installationType === 'pv' ? 'irradiationTilt15Degrees' : 'irradiationTilt60Degrees');
        var areaSum = 0;
        var meanIrradiation = 0;
        for (var iPixel = 0, nPixels = this._roofPixels.length; iPixel < nPixels; ++iPixel) {
            var pixel3DArea = this._properties.mountingType === 'onRoofPlane' ? this._roofPixels[iPixel].area3D : this._properties.pixelGroundSurface;
            areaSum += pixel3DArea;
            meanIrradiation += this._roofPixels[iPixel][irradiationDataSet] * pixel3DArea;
            if (areaSum > usedRoofArea) {
                break;
            }
        }
        meanIrradiation /= areaSum;

        this._properties.meanSpecificAnnualSolarEnergy = meanIrradiation; // in kWh/m²/year
        this._properties.totalAnnualSolarEnergy = this._properties.meanSpecificAnnualSolarEnergy * panelSurface; // in kWh/year
        this._properties.panelSurface = panelSurface;
        this._properties.usedSurfaceArea = usedRoofArea;
    };

    /**
     * Returns the specified roof property
     *
     * @param {String} - Name of the property to be returned
     * @param {Number|String} - Value of the requested property
     */
    module.PixelizedRoof.prototype.getProperty = function(property) {
        return this._properties[property];
    };

    /**
     * @return {Objet} - Roof properties. This object has the following keys:
     *      - totalAnnualSolarEnergy: [kWh]
     *      - meanSpecificAnnualSolarEnergy: [kWh/m2]
     *      - usedSurfaceArea: [m2]
     *      - totalRoofSurfaceArea: [m2]
     *      - meanRoofOrientation: [deg]
     *      - meanRoofSlope: [deg]
     */
    module.PixelizedRoof.prototype.getProperties = function() {
        return this._properties;
    };

    module.PixelizedRoof.prototype._getSurfaceUsageCoefficient = function(installationType, surfaceType) {
        return (this._params && this._params.hasOwnProperty('surfaceUsageCoefficient') &&
            this._params.surfaceUsageCoefficient.hasOwnProperty(installationType) &&
            this._params.surfaceUsageCoefficient[installationType].hasOwnProperty(surfaceType)) ?
                this._params.surfaceUsageCoefficient[installationType][surfaceType] : 1;
    };

    // LES FONCTIONS CI-DESSOUS NE SONT PLUS UTILISEES POUR LE MOMENT

     /**
     * Recherche des point de la surface
     * 
     * @param {Number} fSurface surface a obtenir
     * @param {Function} fnCallBack fonction de callback
     */
    module.PixelizedRoof.prototype._computeData = function(fSurface) {

        //initialisation des attributes
        var currentSurf=0;
       
        //Calcul des voisin de tous les points
        for(var sIdPoint in this._dataPointS){
            var oPoint=this._dataPointS[sIdPoint];
            oPoint.status="other";
            this._setNeighbour(oPoint);
        }

        //initialisation de la liste de point retenue
        this._retainPointA=[];
        
        //recuperation de la liste des surface potentiel
        var oNeighbourA=this._getOrderNeighbour();

        var bNoMore=false;

        //Lancement du calcul recusif
        this._computeOneStep(oNeighbourA,currentSurf,fSurface)

    }


    /**
     * Recherche ajout de/des meilleur cellule
     * 
     * @param {Array} oNeighbourA tableau des point potenciel trier
     * @param {Number} currentSurf surface actuelement obtenue
     * @param {Number} fSurface surface a atteindre
     * @param {Function} fnCallBack fonction de callback
     * 
     */
    module.PixelizedRoof.prototype._computeOneStep = function(oNeighbourA,currentSurf,fSurface) {


        var bNoMore =false;//condition d'arret

        if(oNeighbourA.length<=0){//si pas de point dispo on arrete
            bNoMore=true;
        }    
    
        if(!bNoMore&& currentSurf<fSurface){
            //si surface nom atteint on rajoute un point
            var newPoint = oNeighbourA[0];
            newPoint.status="retain";
            this._retainPointA.push(newPoint);
            currentSurf+=this._cellSurf;
        }else{
            //si la surface est deja atteint

            if(!bNoMore&&oNeighbourA[0].score<this._retainPointA[0].score){
                //si un autre point est plus interessant

                //on ajoute le meilleur autre point et enleve le pire courant
                var sRemoveKey= this._retainPointA[0].id;
                this._retainPointA[0].status="other";
                if(!this._sRemoveKeyCountS.hasOwnProperty(sRemoveKey)){
                    this._sRemoveKeyCountS[sRemoveKey]=0;
                }else if(this._sRemoveKeyCountS[sRemoveKey]<5){
                    this._sRemoveKeyCountS[sRemoveKey]++;
                }else{
                    bNoMore=true
                }

                var newPoint = oNeighbourA[0];
                newPoint.status="retain";
                this._retainPointA.push(newPoint);
                this._retainPointA.splice(0,1);

            }else{
                //si pas de point plus interresant on arrete
                bNoMore=true
            }
        }

        if(!bNoMore){//si recurtion encore possible
            // calcule les nouveau point possible
           oNeighbourA=this._getOrderNeighbour();
            //on continue
           return this._computeOneStep(oNeighbourA,currentSurf,fSurface);
        }else{
            return;
        }         
    }
    
    /**
    * Recuperation des voisin d'un point
    * 
    * @param {Object} oPoint
    */
    module.PixelizedRoof.prototype._setNeighbour = function(oPoint) {

        oPoint.oNeighbourA=[];
        var irradNeigthBour=0;

        if(oPoint.x>0){
            var sNeighID=(oPoint.x-1)+":"+oPoint.y;
            if(this._dataPointS[sNeighID].irrad){
                oPoint.oNeighbourA.push(this._dataPointS[sNeighID]);
                irradNeigthBour+=this._dataPointS[sNeighID].irrad;    
            }  
        }
        if(oPoint.y>0){
            var sNeighID=oPoint.x+":"+(oPoint.y-1);
            if(this._dataPointS[sNeighID].irrad){
                oPoint.oNeighbourA.push(this._dataPointS[sNeighID]);
                irradNeigthBour+=this._dataPointS[sNeighID].irrad; 
            }     
        }
        if(oPoint.x<this._iNbX-1){
            var sNeighID=(oPoint.x+1)+":"+oPoint.y;
            if(this._dataPointS[sNeighID].irrad){
                oPoint.oNeighbourA.push(this._dataPointS[sNeighID]);
                irradNeigthBour+=this._dataPointS[sNeighID].irrad; 
            }
               
        }
        if(oPoint.y<this._iNbY-1){
            var sNeighID=oPoint.x+":"+(oPoint.y+1);
            if(this._dataPointS[sNeighID].irrad){
                oPoint.oNeighbourA.push(this._dataPointS[sNeighID]);
                irradNeigthBour+=this._dataPointS[sNeighID].irrad; 
            }      
        }

        if(oPoint.x>0){
            if(oPoint.y>0){
                var sNeighID=(oPoint.x-1)+":"+(oPoint.y-1);
                if(this._dataPointS[sNeighID].irrad){
                    oPoint.oNeighbourA.push(this._dataPointS[sNeighID]);
                    irradNeigthBour+=this._dataPointS[sNeighID].irrad;   
                }
                   
            }
            if(oPoint.y<this._iNbY-1){
                var sNeighID=(oPoint.x-1)+":"+(oPoint.y+1); 
                if(this._dataPointS[sNeighID].irrad){
                    oPoint.oNeighbourA.push(this._dataPointS[sNeighID]);
                    irradNeigthBour+=this._dataPointS[sNeighID].irrad; 
                }
                    
            }    
        }
        
        if(oPoint.x<this._iNbX-1){
            if(oPoint.y>0){
                var sNeighID=(oPoint.x+1)+":"+(oPoint.y-1);
                if(this._dataPointS[sNeighID].irrad){
                    oPoint.oNeighbourA.push(this._dataPointS[sNeighID]);
                    irradNeigthBour+=this._dataPointS[sNeighID].irrad;  
                }
                      
            }
            if(oPoint.y<this._iNbY-1){
                var sNeighID=(oPoint.x+1)+":"+(oPoint.y+1);
                if(this._dataPointS[sNeighID].irrad){
                    oPoint.oNeighbourA.push(this._dataPointS[sNeighID]); 
                    irradNeigthBour+=this._dataPointS[sNeighID].irrad; 
                }     
            }    
        }

        //ajout de la somme des irrad des voisin  pour le calcul de la note
        oPoint.irradNeigthBour=irradNeigthBour;
    }
    


    /**
     * Recuperation des point possible est ordonacement en function de leur score
     * 
     * @return {Array} tableau des points
     */
    module.PixelizedRoof.prototype._getOrderNeighbour=function(){
     
        var sommeIrrad=0;
        var sommeTilt=0;
        var nbOriS={};
        var nbPoint=0;
        var oNeighbourA=[];


        //Ajout des autre point a la liste
        for(var siD in this._dataPointS){
            var oPoint =this._dataPointS[siD];
            if(oPoint.status!="retain"){
                oPoint.status="other";
                oPoint.count=0;
                oNeighbourA.push(oPoint);
            }
        }

        //calcule des donné moeyen des point retenue en cour
        for (var iNumPoint = 0;iNumPoint<this._retainPointA.length;iNumPoint++){
            var oPoint=this._retainPointA[iNumPoint];

            if(oPoint.irrad)
                sommeIrrad+=oPoint.irrad;
            if(oPoint.tilt)    
                sommeTilt+=oPoint.tilt;
            if(oPoint.ori){    
                if(!nbOriS.hasOwnProperty(oPoint.ori)){
                    nbOriS[oPoint.ori]=0;
                }
                nbOriS[oPoint.ori]++;
            }
            nbPoint++;

            oPoint.count=0;
            //count pour chaque point le nombre de ponit retenuer voisin
            if(oPoint.hasOwnProperty("oNeighbourA")){
                for(var iNumNeigh=0;iNumNeigh<oPoint.oNeighbourA.length;iNumNeigh++){
                    var oNeight=oPoint.oNeighbourA[iNumNeigh];
                    if(oNeight.status=="other"){
                        oNeight.status="neighbour";
                        oNeight.count=1;
                    }else if( oNeight.status=="neighbour"){
                        oNeight.count++;
                    }else(
                        oPoint.count++
                    )
                }    
            }
        }

        var meanTilt=null
        var meanIrrad=null
        var meanOri=null;
        if(nbPoint>0){
            meanTilt=sommeTilt/nbPoint;
            meanIrrad=sommeIrrad/nbPoint;
            var maxOccur=0;
            for(var ori in nbOriS){
                if(nbOriS[ori]>maxOccur){
                    maxOccur=nbOriS[ori];
                    meanOri=parseInt(ori);
                }  
            }
        }

        //calcule du score des ponit
        for(var iNumPoint=0;iNumPoint<oNeighbourA.length;iNumPoint++){
            var oNeigt=oNeighbourA[iNumPoint];
            this._setPointScore(oNeigt,meanTilt,meanIrrad,meanOri);
        }

        //calcule du score des ponit
        for (var iNumPoint = 0;iNumPoint<this._retainPointA.length;iNumPoint++){
            var oPoint=this._retainPointA[iNumPoint];
            this._setPointScore(oPoint,meanTilt,meanIrrad,meanOri);
        }

    
        //ordoancement des ponit
        oNeighbourA.sort(this._orderFunction);
        this._retainPointA.sort(this._orderFunction);
        this._retainPointA.reverse();


        return oNeighbourA;
    }

    /**
     * Function d'ordonancement des ponit en function de leurs scrore
     * @param {Object} oPointA
     * @param {Object} oPointB
     * 
     * @return 0,1,-1
     */
    module.PixelizedRoof.prototype._orderFunction=function(oPointA,oPointB){
        if(oPointA.score==null)
            return 1;
        if(oPointB.score==null)    
            return -1;
        if(oPointA.score<oPointB.score){
            return -1;
        }  
        if(oPointB.score<oPointA.score){
            return 1;
        }

        if(oPointA.irrad<oPointB.irrad)
            return 1;
        if(oPointA.count<oPointB.count)
            return 1;
       
           

        return 0;
    }


    /**
     * Calcule du score du point
     * 
     * @param {Object} oPoint point a calculer
     * @param {Number} fTilt tilt de refference
     * @param {Number} fIrrad irrad de refference
     * @param {Number} iOri ori de reffernence
     */
    module.PixelizedRoof.prototype._setPointScore=function(oPoint,fTilt,fIrrad,iOri){
        var stepIrrad=1;
        if(fIrrad!=null&&fIrrad>0)
            stepIrrad=fIrrad/100;
        
        if(oPoint.irrad){
            var pointMeanIrrad=(oPoint.irrad+oPoint.irradNeigthBour)/(oPoint.oNeighbourA.length+1);
            var iScoreIrrad=(fIrrad-pointMeanIrrad)/stepIrrad;
            var iScoreOri=0;
            if(iOri!=null){
                iScoreOri=Math.abs(iOri-oPoint.ori);
                if(iScoreOri>180)
                    iScoreOri=360-iScoreOri;

              
                if(iScoreOri>=fBadOriSeuil)  
                    iScoreOri-=fBadOriWeight;   
                else
                    iScoreOri=0; 
            }
            var iScoreTilt=0;
            if(fTilt!=null){
                iScoreTilt=Math.abs(fTilt-oPoint.tilt);
               
               if(iScoreTilt>=fBadTiltSeuil)    
                    iScoreTilt-=fBadOriWeight;   
                else
                    iScoreTilt=0;
            }
            oPoint.score=iScoreIrrad+iScoreOri+iScoreTilt;

        }else{
            oPoint.score=10000;
        }  
        if(oPoint.count==0){
            oPoint.score-=fNoNeightBourWieight;
        }
        oPoint.score-=(oPoint.count*fNeightBourWieight);   
    }  
    
    module.PixelizedRoof.prototype._displayFullImage=function (fnCallBack){
        var _this =this;
        var iRatio=20;
        var imageType=["Ori","Tilt","Irrad","Comput"];

        var retainKeyA=[];
      
        for(var siD in this._dataPointS){
            var oPoint =this._dataPointS[siD];
            if(oPoint.status=="retain"){
                retainKeyA.push(oPoint.x+":"+oPoint.y);
            }
        }        
            
        var image = new Jimp(this._iNbX*iRatio*4,this._iNbY*iRatio, function (err, image) {
           image.rgba( true ); // pas de transparence
           image.deflateLevel( 9 ); //0 (peu compressé),  1 à 9 (très compressé)
           image.deflateStrategy( 1 ); // 0 => 5.8s, 536 kO, 1 => 5.8s, 535 kO,  2 = 235s, 2319 kO, 3 = 2.5s, 2166 kO,  3 par defaut
           for(var imageNum=0;imageNum<4;imageNum++){
               var sImageType=imageType[imageNum];
                for(var x=0;x<_this._iNbX;x++){
                    for(var xCount=0;xCount<iRatio;xCount++){
                        for(var y=0;y<_this._iNbY;y++){
                            var index =(y*_this._iNbX+x)*4;
                            for(var yCount=0;yCount<iRatio;yCount++){
                                switch(sImageType){
                                    case "Irrad":
                                        image.setPixelColor(Jimp.rgbaToInt(_this._imageData[index],0,0,_this._imageData[index+3]), x*iRatio+xCount+imageNum*_this._iNbX*iRatio,y*iRatio+yCount);
                                        break;
                                    case "Ori":
                                        image.setPixelColor(Jimp.rgbaToInt(0,0,_this._imageData[index+2],_this._imageData[index+3]), x*iRatio+xCount+imageNum*_this._iNbX*iRatio,y*iRatio+yCount);
                                        break;
                                    case "Tilt":
                                        image.setPixelColor(Jimp.rgbaToInt(0,_this._imageData[index+1],0,_this._imageData[index+3]), x*iRatio+xCount+imageNum*_this._iNbX*iRatio,y*iRatio+yCount); // sets the colour of that pixel
                                        break;
                                    default:
                                        if(retainKeyA.indexOf(x+":"+y)>=0){
                                            image.setPixelColor(Jimp.rgbaToInt(0,255,0,255), x*iRatio+xCount+imageNum*_this._iNbX*iRatio,y*iRatio+yCount); // sets the colour of that pixel
                                        }else{
                                            image.setPixelColor(Jimp.rgbaToInt(0,0,0,_this._imageData[index+3]), x*iRatio+xCount+imageNum*_this._iNbX*iRatio,y*iRatio+yCount); // sets the colour of that pixel
                                        }
                                }
                            }    
                        }
                    }
                }
           }
           
           image.getBase64(Jimp.MIME_PNG, function (err, src) {
                if(typeof $ != "undefined"){
                    var img = document.createElement("img");
                    img.setAttribute("src", src);
                    console.log($("#rasterImage"))
                    $("#rasterImage").html("");
                    $("#rasterImage").append(img);
                }

                fnCallBack(image);
            });
        });
    }

    return module;

})(am || {});



//=============================================
// AM.UTILS NAMESPACE
//=============================================
am.utils = (function(module) {

    // Use strict mode
    'use strict';

    /**
     * Returns a new instance of class am.PixelizedRoof
     *
     * @param {Object} config - Project configuration (see constructor of class am.PixelizedRoof for more information)
     * @param {Object} params - Object configuration (see constructor of class am.PixelizedRoof for more information)
     * @return {am.PixelizedRoof} - New instance of class am.PixelizedRoof 
     */
    module.pixelizedRoof = function(config, params) {
        var model = new am.PixelizedRoof();
        model.init(config, params);
        return model;
    };

    return module;

})(am.utils || {});